import { DefaultRawDatum, ResponsivePie } from '@nivo/pie';
import { useTranslation } from 'react-i18next';

import { Skeleton } from '@/components/ui/skeleton';

import styles from './Pie.module.scss';

interface Props {
  data?: (DefaultRawDatum & { label?: string | number })[];
  Tooltip?: React.ReactElement;
  innerRadius?: number;
  hasCenterMetric?: boolean;
  padAngle?: number;
  multiColor?: boolean;
  arcLabelsRadiusOffset?: number;
  enableArcLinkLabels?: boolean;
  enableArcLabels?: boolean;
  arcLinkLabelsDiagonalLength?: number;
  arcLinkLabelsStraightLength?: number;
  hasMargin?: boolean;
  arcLabelsSkipAngle?: number;
  arcLinkLabelsSkipAngle?: number;
  colors?: string[];
}

const pieColors = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-6)',
  'var(--chart-7)',
];

const CenteredMetric = ({
  dataWithArc,
  innerRadius,
}: {
  dataWithArc: { value: number; label: string }[];
  innerRadius: number;
}) => {
  const { t } = useTranslation();
  const centerPercentage = dataWithArc[1].value;
  const centerTitle = dataWithArc[1]?.label;

  return (
    <foreignObject width="100%" height="100%">
      <div className={styles.wrapper}>
        <div
          className={styles.inner_wrapper}
          style={{ width: innerRadius * 2, height: innerRadius * 2 }}
        >
          <p className={styles.middle_percent}>
            {t('common.percentage', { 0: Math.round(centerPercentage) })}
          </p>
          {centerTitle && <p className={styles.middle_text}>{centerTitle}</p>}
        </div>
      </div>
    </foreignObject>
  );
};

const PieChart = ({
  data,
  hasCenterMetric = true,
  padAngle = 0,
  multiColor = false,
  arcLabelsRadiusOffset = 0.5,
  arcLinkLabelsDiagonalLength = 16,
  arcLinkLabelsStraightLength = 24,
  enableArcLinkLabels = false,
  enableArcLabels = false,
  Tooltip,
  innerRadius = 0.624,
  hasMargin = false,
  arcLabelsSkipAngle = 0,
  arcLinkLabelsSkipAngle = 0,
  colors = ['var(--chart-2)', 'var(--primary)'],
}: Props) => {
  const { t } = useTranslation();
  if (!data) {
    return (
      <div className={styles.chart}>
        <Skeleton className="h-40 w-40 rounded-full" />
      </div>
    );
  }

  return (
    <div className={styles.chart}>
      <ResponsivePie
        data={data}
        innerRadius={innerRadius}
        tooltip={() => Tooltip ?? null}
        margin={hasMargin ? { top: 30, right: 20, bottom: 20, left: 30 } : {}}
        colors={multiColor ? pieColors : colors}
        enableArcLinkLabels={enableArcLinkLabels}
        enableArcLabels={enableArcLabels}
        padAngle={padAngle}
        arcLabelsRadiusOffset={arcLabelsRadiusOffset}
        arcLabelsTextColor="var(--primary-foreground)"
        arcLinkLabelsDiagonalLength={arcLinkLabelsDiagonalLength}
        arcLinkLabelsStraightLength={arcLinkLabelsStraightLength}
        arcLinkLabelsColor={{ from: 'color' }}
        arcLabelsSkipAngle={arcLabelsSkipAngle}
        arcLinkLabelsSkipAngle={arcLinkLabelsSkipAngle}
        arcLabel={(d: { value: number }) => t('common.percentage', { 0: Math.round(d.value) })}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore: Types in ResponsivePie are wrong
        layers={
          hasCenterMetric
            ? [CenteredMetric, 'radialLabels', 'arcs']
            : ['arcs', 'arcLabels', 'arcLinkLabels', 'legends']
        }
      />
    </div>
  );
};

export default PieChart;
